import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import Image from 'components/Common/Image/Image'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import RenderInclusions from 'components/OfferPage/AlwaysOn/RenderInclusions'
import BaseBundleOptionItemTile from 'components/OfferPage/BundleAndSave/BundleAndSaveTiles/BaseBundleOptionItemTile'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import ModalContext from 'contexts/ModalContext'
import { takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { nonNullable } from 'lib/array/arrayUtils'
import moment from 'moment/moment'
import React, { useCallback, useContext, useMemo } from 'react'
import BundleAndSaveHotelTileInclusionsModalView from './BundleAndSaveHotelTileInclusionsModalView'

interface Props {
  offer: App.Offer
  pkg: App.Package
  className?: string
  selectedDuration: number;
  checkIn?: string,
  rooms: Array<App.RoomOccupants>;
  availableForPurchase: boolean;
}

function BundleAndSaveHotelTile(props: Props) {
  const {
    offer,
    rooms,
    pkg,
    className,
    selectedDuration,
    checkIn,
    availableForPurchase,
  } = props
  const showModal = useContext(ModalContext)

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(pkg.duration, 'days'), [checkInMoment, pkg.duration])
  const { inclusions, bonusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const onInclusionReveal = useCallback(() => {
    showModal(<BundleAndSaveHotelTileInclusionsModalView
      offer={offer}
      packageView={pkg}
      inclusions={inclusions}
      bonusInclusions={bonusInclusions}
      amenities={pkg.roomType!.amenities}
      roomPolicyDescription={pkg.roomPolicyDescription}
    />)
  }, [bonusInclusions, inclusions, offer, pkg, showModal])

  const locationLabel = useMemo(() => {
    return nonNullable([
      offer.locationHeading,
      offer.locationSubheading,
    ]).join(', ')
  }, [offer])

  return (
    <BaseBundleOptionItemTile className={className} room={pkg.roomType!}>
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={4}>
          <div>
            {!!locationLabel && <BodyText variant="small">{locationLabel}</BodyText>}
            <Heading variant="heading5">{offer.property?.name}</Heading>
          </div>

          {offer.property?.logoImageId && <CSSBreakpoint min="tablet">
            <Image
              dpr={2}
              enhancedFormats={false}
              id={offer.property.logoImageId}
              width={80}
              aspectRatio="2"
              alt={`${offer.property.name || 'Vendor'} logo`}
            />
          </CSSBreakpoint>}
        </Group>

        <div>
          {!!pkg.roomType?.name && <BodyText variant="large" weight="bold" colour="neutral-two">
            {pkg.roomType?.name}
          </BodyText>}
          {!!pkg.roomType?.sizeSqm && <BodyText variant="medium">
            <b>Size: </b> {pkg.roomType.sizeSqm} m<sup>2</sup>
          </BodyText>}
          {availableForPurchase && offer.type === OFFER_TYPE_HOTEL && <FlashCancellationPolicies
            cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
            checkInDate={checkIn}
            offerId={pkg.offerId}
          />}
          {availableForPurchase && offer.type === OFFER_TYPE_ALWAYS_ON && <TAOCancellationPolicies
            offerId={offer.id}
            useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
            timezoneOffset={offer.property?.timezoneOffset}
            uniqueKey={pkg.uniqueKey}
            checkInDate={checkIn}
            checkOutDate={checkIn ? moment(checkIn).add(selectedDuration, 'days').format(ISO_DATE_FORMAT) : undefined}
            occupants={rooms}
            cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
          />}
        </div>

        <Group direction="vertical" gap={4}>
          <LargeContentToggle hideText maxHeight={164}>
            {offer.type === OFFER_TYPE_ALWAYS_ON && (
              <RenderInclusions
                bonusInclusions={bonusInclusions}
                inclusions={inclusions}
              />)}
            {offer.type === OFFER_TYPE_HOTEL && (<InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} />)}
          </LargeContentToggle>
          {pkg.roomType &&
            <TextLink
              size="medium"
              weight="bold"
              onClick={onInclusionReveal}>
              View all inclusions & details
            </TextLink>
          }
        </Group>
      </Group>
    </BaseBundleOptionItemTile>
  )
}

export default withOfferPageState((state, props) => {
  const currentOfferId = props.offer.id
  const currentState = takeOfferState(state, currentOfferId)

  return {
    selectedDuration: currentState.duration,
    rooms: currentState.rooms,
    checkIn: currentState.checkInDate,
  }
})(BundleAndSaveHotelTile)
